import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  limitReviews,
  operations,
  pipes,
  policyholders,
  type LimitReviewRequest,
  type PipeCardDetail,
  type PipeSummary,
} from '@/shared/api/resources';
import { formatCpfCnpj, moneyInputError, parseMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { gruposDeModalidade } from '../policyholders/rotulos';
import { fieldErrorOf } from '../shared/fieldError';

/**
 * Esteiras em que a pessoa abre o cartao no quadro (P-EST-2), com o objeto e os tipos
 * de cartao de cada uma. Cadastro abre o cartao do tomador; PEP e Resseguro, o da
 * cotacao. Credito e Subscricao abrem pela etapa da operacao, e Nomeacao, pelo pedido.
 */
const aberturaPorEsteira: Record<
  string,
  { objeto: 'tomador' | 'operacao'; tipos: Array<{ value: string; label: string }> }
> = {
  cadastro: {
    objeto: 'tomador',
    tipos: [
      { value: 'pendencia_cadastro', label: 'Pendencia de cadastro' },
      { value: 'definicao_grupo_economico', label: 'Definicao de grupo economico' },
      { value: 'credito_spe_consorcio', label: 'Credito SPE/Consorcio' },
    ],
  },
  pep: { objeto: 'operacao', tipos: [{ value: 'pep', label: 'PEP' }] },
  resseguro: { objeto: 'operacao', tipos: [{ value: 'cotacao', label: 'Cotacao' }] },
};

/**
 * "Abrir cartao" do quadro: escolhe o tomador ou a cotacao e o tipo do cartao. O
 * cartao nasce na coluna inicial da esteira, sem decidir nada.
 */
export function PipeOpenCardDialog({
  pipe,
  open,
  onClose,
  onOpened,
}: {
  pipe: Pick<PipeSummary, 'id' | 'key' | 'name'>;
  open: boolean;
  onClose: () => void;
  onOpened?: (card: PipeCardDetail) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const regra = aberturaPorEsteira[pipe.key] ?? aberturaPorEsteira.resseguro;
  const [busca, setBusca] = useState('');
  const [objeto, setObjeto] = useState('');
  const [tipo, setTipo] = useState(regra?.tipos[0]?.value ?? '');
  const [titulo, setTitulo] = useState('');

  const tomadores = useQuery({
    queryKey: ['policyholders', 'abrir-cartao', busca],
    queryFn: ({ signal }) => policyholders.list({ q: busca, limit: 10 }, signal),
    enabled: open && regra?.objeto === 'tomador' && busca.trim().length >= 2,
  });
  const cotacoes = useQuery({
    queryKey: ['operations', 'abrir-cartao', busca],
    queryFn: ({ signal }) => operations.list({ q: busca, limit: 10 }, signal),
    enabled: open && regra?.objeto === 'operacao' && busca.trim().length >= 2,
  });

  const abrir = useMutation({
    mutationFn: () =>
      pipes.openCard(pipe.id, {
        subject_type: regra?.objeto ?? 'operacao',
        subject_id: objeto,
        card_type: tipo,
        ...(titulo.trim() ? { title: titulo.trim() } : {}),
      }),
    onSuccess: (cartao) => {
      toast.success('Cartao aberto.');
      void queryClient.invalidateQueries({ queryKey: ['pipes'] });
      fechar();
      onOpened?.(cartao);
    },
  });

  function fechar() {
    setBusca('');
    setObjeto('');
    setTitulo('');
    abrir.reset();
    onClose();
  }

  const apiError = abrir.error instanceof ApiError ? abrir.error : null;
  const opcoes =
    regra?.objeto === 'tomador'
      ? (tomadores.data?.items ?? []).map((t) => ({
          value: t.id,
          label: `${t.legal_name} (${formatCpfCnpj(t.document)})`,
        }))
      : (cotacoes.data?.items ?? []).map((o) => ({
          value: o.id,
          label: `Cotacao ${o.proposal_number || o.number} - ${o.policyholder.label}`,
        }));

  return (
    <Modal
      open={open}
      onClose={fechar}
      title={`Abrir cartao em ${pipe.name}`}
      description="O cartao nasce na coluna inicial da esteira e nao decide nada. Abrir fica no historico do cartao."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={fechar}>
            Cancelar
          </Button>
          <Button
            disabled={objeto === '' || tipo === ''}
            loading={abrir.isPending}
            onClick={() => abrir.mutate()}
          >
            Abrir cartao
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {abrir.isError &&
        !fieldErrorOf(apiError, 'subject_id') &&
        !fieldErrorOf(apiError, 'title') ? (
          <Alert tone="danger" title="Nao foi possivel abrir o cartao">
            {abrir.error.message}
          </Alert>
        ) : null}
        {regra && regra.tipos.length > 1 ? (
          <Select
            label="Tipo do cartao"
            options={regra.tipos}
            value={tipo}
            onChange={(event) => setTipo(event.target.value)}
          />
        ) : null}
        <Field
          label={regra?.objeto === 'tomador' ? 'Buscar tomador' : 'Buscar cotacao'}
          value={busca}
          placeholder={
            regra?.objeto === 'tomador'
              ? 'Nome ou CNPJ'
              : 'Numero da cotacao ou tomador'
          }
          hint="Digite ao menos dois caracteres."
          onChange={(event) => {
            setBusca(event.target.value);
            setObjeto('');
          }}
        />
        <Select
          label={regra?.objeto === 'tomador' ? 'Tomador' : 'Cotacao'}
          placeholder={opcoes.length > 0 ? 'Escolha' : 'Nenhum resultado'}
          options={opcoes}
          value={objeto}
          error={fieldErrorOf(apiError, 'subject_id')}
          onChange={(event) => setObjeto(event.target.value)}
        />
        <Field
          label="Titulo do cartao"
          value={titulo}
          maxLength={160}
          hint="Opcional. Sem titulo, o cartao leva o nome do tipo."
          error={fieldErrorOf(apiError, 'title')}
          onChange={(event) => setTitulo(event.target.value)}
        />
      </div>
    </Modal>
  );
}

/**
 * Pedido de revisao de limite (cartao Revisao de Limite da esteira Credito). Pedir nao
 * muda o limite: o limite novo vem da conclusao do pedido, pela alcada do credito, e
 * aparece em Limites e Taxas. O valor e digitado com virgula e vai em centavos, sem ponto
 * flutuante.
 */
export function LimitReviewRequestDialog({
  open,
  onClose,
  policyholder,
  pendingGroups = [],
  onRequested,
}: {
  open: boolean;
  onClose: () => void;
  /** O tomador ja escolhido (detalhe do tomador). Sem ele, a pessoa busca. */
  policyholder?: { id: string; label: string } | undefined;
  /** Grupos com pedido pendente, que nao aceitam outro. */
  pendingGroups?: string[] | undefined;
  onRequested?: (pedido: LimitReviewRequest) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [busca, setBusca] = useState('');
  const [tomador, setTomador] = useState('');
  const [grupo, setGrupo] = useState('');
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');

  const tomadores = useQuery({
    queryKey: ['policyholders', 'revisao-de-limite', busca],
    queryFn: ({ signal }) => policyholders.list({ q: busca, limit: 10 }, signal),
    enabled: open && !policyholder && busca.trim().length >= 2,
  });

  const valorEmDinheiro = parseMoney(valor);
  const pedir = useMutation({
    mutationFn: () => {
      if (!valorEmDinheiro) throw new Error('Informe o limite pretendido.');
      return limitReviews.request({
        policyholder_id: policyholder?.id ?? tomador,
        modality_group: grupo,
        requested_amount: valorEmDinheiro,
        ...(motivo.trim() ? { reason: motivo.trim() } : {}),
      });
    },
    onSuccess: (pedido) => {
      toast.success('Pedido de revisao de limite registrado. O limite nao mudou.');
      void queryClient.invalidateQueries({ queryKey: ['limit-reviews'] });
      void queryClient.invalidateQueries({ queryKey: ['pipes'] });
      fechar();
      onRequested?.(pedido);
    },
  });

  function fechar() {
    setBusca('');
    setTomador('');
    setGrupo('');
    setValor('');
    setMotivo('');
    pedir.reset();
    onClose();
  }

  const apiError = pedir.error instanceof ApiError ? pedir.error : null;
  const grupos = Object.entries(gruposDeModalidade)
    .filter(([chave]) => !pendingGroups.includes(chave))
    .map(([value, label]) => ({ value, label }));
  const erroDoValor = moneyInputError(valor);
  const valorPositivo = valorEmDinheiro !== null && valorEmDinheiro.amount > 0;
  const completo =
    (policyholder?.id ?? tomador) !== '' && grupo !== '' && valorPositivo;

  return (
    <Modal
      open={open}
      onClose={fechar}
      title="Pedir revisao de limite"
      description="O pedido abre o cartao Revisao de Limite na esteira Credito. O limite novo vale quando o credito conclui o pedido, e aparece em Limites e Taxas."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={fechar}>
            Cancelar
          </Button>
          <Button
            disabled={!completo || erroDoValor !== undefined}
            loading={pedir.isPending}
            onClick={() => pedir.mutate()}
          >
            Pedir revisao
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {pedir.isError &&
        !fieldErrorOf(apiError, 'requested_amount') &&
        !fieldErrorOf(apiError, 'modality_group') ? (
          <Alert tone="danger" title="Nao foi possivel registrar o pedido">
            {pedir.error.message}
          </Alert>
        ) : null}
        {policyholder ? (
          <p className="text-sm text-ink-700">
            <span className="font-medium">Tomador:</span> {policyholder.label}
          </p>
        ) : (
          <>
            <Field
              label="Buscar tomador"
              value={busca}
              placeholder="Nome ou CNPJ"
              hint="Digite ao menos dois caracteres."
              onChange={(event) => {
                setBusca(event.target.value);
                setTomador('');
              }}
            />
            <Select
              label="Tomador"
              placeholder={
                (tomadores.data?.items ?? []).length > 0
                  ? 'Escolha'
                  : 'Nenhum resultado'
              }
              options={(tomadores.data?.items ?? []).map((t) => ({
                value: t.id,
                label: `${t.legal_name} (${formatCpfCnpj(t.document)})`,
              }))}
              value={tomador}
              error={fieldErrorOf(apiError, 'policyholder_id')}
              onChange={(event) => setTomador(event.target.value)}
            />
          </>
        )}
        <Select
          label="Grupo de modalidades"
          placeholder="Escolha"
          options={grupos}
          value={grupo}
          error={fieldErrorOf(apiError, 'modality_group')}
          hint="Um pedido pendente por grupo."
          onChange={(event) => setGrupo(event.target.value)}
        />
        <MoneyField
          label="Limite pretendido"
          value={valor}
          error={erroDoValor ?? fieldErrorOf(apiError, 'requested_amount')}
          onValueChange={setValor}
        />
        <Textarea
          label="Motivo do pedido"
          value={motivo}
          maxLength={2000}
          error={fieldErrorOf(apiError, 'reason')}
          onChange={(event) => setMotivo(event.target.value)}
        />
      </div>
    </Modal>
  );
}
